import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { api } from '../services/api';
import './Projects.css';

const getYouTubeId = (url) => {
  if (!url) return null;
  const patterns = [
    /[?&]v=([a-zA-Z0-9_-]{6,})/,
    /youtu\.be\/([a-zA-Z0-9_-]{6,})/,
    /youtube\.com\/embed\/([a-zA-Z0-9_-]{6,})/,
    /youtube\.com\/shorts\/([a-zA-Z0-9_-]{6,})/
  ];
  for (const re of patterns) {
    const m = url.match(re);
    if (m) return m[1];
  }
  return null;
};

const getThumbnailFor = (project) => {
  if (project.media_type === 'image' && project.media_url) {
    return project.media_url;
  }
  if (project.media_type === 'video') {
    const id = getYouTubeId(project.media_url);
    if (id) return `https://img.youtube.com/vi/${id}/hqdefault.jpg`;
    // Cloudinary generates a poster frame for every uploaded video: same URL,
    // .jpg instead of .mp4.
    if (/\/video\/upload\/.+\.mp4$/.test(project.media_url || '')) {
      return project.media_url.replace(/\.mp4$/, '.jpg');
    }
  }
  return null;
};

const pickLocalized = (project, lang) => {
  const isPolish = (lang || '').toLowerCase().startsWith('pl');
  const pl = (v) => (typeof v === 'string' && v.trim() ? v : null);
  return {
    title: (isPolish && pl(project.title_pl)) || project.title,
    description:
      (isPolish && pl(project.short_description_pl)) || project.short_description
  };
};

// Which values of the filter field this group actually contains, so the row
// can never offer an empty tab or miss a new one. A field the backend does
// not send yet yields [], and no filter row is rendered.
function useGroupFacet(projectType, field) {
  const [values, setValues] = useState(() => api.peekGroupFacet(projectType, field) || []);

  useEffect(() => {
    let cancelled = false;
    api.getGroupFacet(projectType, field)
      .then(list => { if (!cancelled) setValues(list); })
      .catch(() => { if (!cancelled) setValues([]); });
    return () => { cancelled = true; };
  }, [projectType, field]);

  return values;
}

function useProjectList(filters, projectType, featured) {
  // On a prerendered page the build-time list is the starting state, so the
  // first render matches the HTML already on screen. It is refreshed quietly
  // right after: no loading state, and a failed refresh keeps it.
  const [preloaded] = useState(() =>
    featured ? api.peekFeaturedProjects(projectType) : api.peekProjects(filters, projectType));
  const quietRefresh = useRef(Boolean(preloaded));
  const [projects, setProjects] = useState(preloaded?.results || []);
  const [loading, setLoading] = useState(!preloaded);
  const [error, setError] = useState(null);
  const [nextUrl, setNextUrl] = useState(preloaded?.next || null);
  const [loadingMore, setLoadingMore] = useState(false);
  const [moreError, setMoreError] = useState(null);
  // Bumped on every filter change so in-flight responses from a previous
  // filter can't append their page onto the new list.
  const requestId = useRef(0);

  useEffect(() => {
    const id = ++requestId.current;
    const quiet = quietRefresh.current;
    quietRefresh.current = false;
    const load = async () => {
      try {
        if (!quiet) {
          setLoading(true);
          setError(null);
          setMoreError(null);
          setNextUrl(null);
          setLoadingMore(false);
        }
        const data = featured
          ? await api.getFeaturedProjects(projectType)
          : await api.getProjects(filters, projectType);
        if (id !== requestId.current) return;
        setProjects(Array.isArray(data?.results) ? data.results : []);
        setNextUrl(data?.next || null);
      } catch (e) {
        if (id === requestId.current && !quiet) setError(e.message || 'Failed to load projects');
      } finally {
        if (id === requestId.current) setLoading(false);
      }
    };
    load();
  }, [filters, projectType, featured]);

  const loadMore = async () => {
    if (!nextUrl || loadingMore) return;
    const id = requestId.current;
    try {
      setLoadingMore(true);
      setMoreError(null);
      const data = await api.getProjectFromUrl(nextUrl);
      if (id !== requestId.current) return;
      setProjects(prev => [
        ...prev,
        ...(Array.isArray(data?.results) ? data.results : [])
      ]);
      setNextUrl(data?.next || null);
    } catch (e) {
      // Keep the already-loaded projects on screen — only the extra page failed.
      if (id === requestId.current) setMoreError(e.message || 'Failed to load projects');
    } finally {
      if (id === requestId.current) setLoadingMore(false);
    }
  };

  return { projects, loading, error, nextUrl, loadingMore, moreError, loadMore };
}

/*
 * One section per project type. Client work sits directly under the hero as
 * proof; the demo catalogue sits near the bottom, where browsing makes sense.
 *
 * `featured` shows only the hand-picked subset (the home page's shortlist);
 * `moreLink` then replaces "load more" with a link to the full catalogue.
 * `fixedFilters` narrows the list server-side for good (a service page shows
 * only its own kind of work) and must be a stable object; `copyKey` points
 * the heading at other locale keys.
 */
function Projects({
  group = 'production',
  facets = ['industry', 'category'],
  featured = false,
  moreLink = null,
  fixedFilters = null,
  copyKey = `projects.groups.${group}`
}) {
  const { t, i18n } = useTranslation();
  // One selected value per facet; the object identity only changes on a click,
  // so it is safe to use directly as an effect dependency.
  const [selected, setSelected] = useState({});
  const filters = useMemo(
    () => (fixedFilters ? { ...fixedFilters, ...selected } : selected),
    [fixedFilters, selected]
  );
  const list = useProjectList(filters, group, featured);
  const { projects, loading, error, nextUrl, loadingMore, moreError, loadMore } = list;

  const choose = (facet, value) =>
    setSelected(prev => ({ ...prev, [facet]: value }));

  return (
    <section
      id={group === 'production' ? 'projects' : 'demos'}
      className="projects-section"
    >
      <div className="projects-group-head">
        <p className="section-eyebrow">{t(`${copyKey}.eyebrow`)}</p>
        <h2 className="projects-group-title">
          <Trans i18nKey={`${copyKey}.title`} components={{ em: <em /> }} />
        </h2>
        <p className="projects-group-desc">{t(`${copyKey}.desc`)}</p>
      </div>

      {facets.map(facet => (
        <FilterRow
          key={facet}
          facet={facet}
          group={group}
          active={selected[facet] || 'all'}
          onSelect={value => choose(facet, value)}
          t={t}
        />
      ))}

      {loading && (
        <div className="projects-state">{t('projects.loading')}</div>
      )}

      {!loading && error && (
        <div className="projects-state error">
          {t('projects.error')} <span className="mono">— {error}</span>
        </div>
      )}

      {!loading && !error && projects.length === 0 && (
        <div className="projects-state">{t('projects.empty')}</div>
      )}

      <div className="projects-grid">
        {projects.map((project, idx) => (
          <ProjectCard
            key={project.id ?? `${project.title}-${idx}`}
            project={project}
            t={t}
            lang={i18n.language}
          />
        ))}
      </div>

      {(nextUrl || moreError) && (
        <div className="projects-more">
          {nextUrl && (
            <button
              type="button"
              className="load-more-btn"
              onClick={loadMore}
              disabled={loadingMore}
            >
              {loadingMore ? t('projects.loadingMore') : t('projects.loadMore')}
            </button>
          )}
          {moreError && (
            <p className="projects-more-error">
              {t('projects.error')} <span className="mono">— {moreError}</span>
            </p>
          )}
        </div>
      )}

      {moreLink && !loading && !error && (
        <div className="projects-more">
          <Link to={moreLink} className="load-more-btn">
            {t('projects.seeAll')} →
          </Link>
        </div>
      )}
    </section>
  );
}

// A labelled row of chips for one facet. Renders nothing when the field is
// missing from the API or holds a single value across the whole group.
function FilterRow({ facet, group, active, onSelect, t }) {
  const values = useGroupFacet(group, facet);
  if (values.length < 2) return null;

  // Prefer a translated label, fall back to whatever the API called it.
  const label = ({ value, label: apiLabel }) =>
    value === 'all'
      ? t('projects.filters.all')
      : t(`projects.${facet}.${value}`, {
          defaultValue: t(`projects.filters.${value}`, { defaultValue: apiLabel || value })
        });

  const caption = t(`projects.facets.${facet}`, { defaultValue: facet });
  const options = [{ value: 'all' }, ...values];

  // Chips on wide screens, a select on narrow ones — twelve industry chips
  // are unreadable on a phone. Both are always rendered and swapped in CSS,
  // so there is no resize listener and both drive the same state.
  return (
    <div className="filters">
      <span className="filters-label">{caption}</span>

      <div className="filter-chips">
        {options.map(entry => (
          <button
            key={entry.value}
            type="button"
            onClick={() => onSelect(entry.value)}
            className={`filter-btn ${active === entry.value ? 'active' : ''}`}
          >
            {label(entry)}
          </button>
        ))}
      </div>

      <select
        className="filter-select"
        aria-label={caption}
        value={active}
        onChange={event => onSelect(event.target.value)}
      >
        {options.map(entry => (
          <option key={entry.value} value={entry.value}>{label(entry)}</option>
        ))}
      </select>
    </div>
  );
}

function ProjectCard({ project, t, lang }) {
  const thumb = getThumbnailFor(project);
  const isVideo = project.media_type === 'video';
  const tags = project.tags || [];
  const projectType = (project.project_type || '').toLowerCase();
  const { title, description } = pickLocalized(project, lang);

  const primaryHref =
    (isVideo && project.media_url) ||
    project.demo_url ||
    project.case_study_post ||
    project.github_repo ||
    null;

  const openCard = () => {
    if (primaryHref) window.open(primaryHref, '_blank', 'noopener,noreferrer');
  };

  const onKey = (e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      openCard();
    }
  };

  return (
    <article
      className={`card ${primaryHref ? 'is-clickable' : ''}`}
      onClick={primaryHref ? openCard : undefined}
      onKeyDown={primaryHref ? onKey : undefined}
      role={primaryHref ? 'link' : undefined}
      tabIndex={primaryHref ? 0 : undefined}
    >
      <div className="card-accent" />
      <div className="card-media">
        {thumb ? (
          <img src={thumb} alt={title} loading="lazy" />
        ) : (
          <span className="card-placeholder">
            {isVideo ? t('projects.media.videoPlaceholder') : t('projects.media.imagePlaceholder')}
          </span>
        )}
        {isVideo && <span className="play-btn" aria-hidden="true" />}
      </div>

      <div className="card-body">
        <div className="card-meta">
          <span className="card-type">
            {t(`projects.category.${(project.category || '').toLowerCase()}`,
               { defaultValue: project.category || '' })}
          </span>
          {projectType && (
            <span className={`card-badge card-badge--${projectType}`}>
              {t(`projects.type.${projectType}`, { defaultValue: projectType })}
            </span>
          )}
        </div>
        <h3 className="card-title">{title}</h3>
        {description && (
          <p className="card-desc">{description}</p>
        )}

        {tags.length > 0 && (
          <div className="card-tags">
            {tags.map(tag => (
              <span key={tag.slug || tag.name} className="tag">{tag.name}</span>
            ))}
          </div>
        )}

        <div className="card-links">
          {isVideo && project.media_url && (
            <a
              href={project.media_url}
              target="_blank"
              rel="noopener noreferrer"
              onClick={e => e.stopPropagation()}
            >{t('projects.links.watch')} →</a>
          )}
          {project.demo_url && (
            <a
              href={project.demo_url}
              target="_blank"
              rel="noopener noreferrer"
              onClick={e => e.stopPropagation()}
            >{t('projects.links.visit')} →</a>
          )}
          {project.case_study_post && (
            <a
              href={project.case_study_post}
              target="_blank"
              rel="noopener noreferrer"
              onClick={e => e.stopPropagation()}
            >{t('projects.links.caseStudy')} →</a>
          )}
          {project.github_repo && (
            <a
              href={project.github_repo}
              target="_blank"
              rel="noopener noreferrer"
              onClick={e => e.stopPropagation()}
            >{t('projects.links.code')} →</a>
          )}
        </div>
      </div>
    </article>
  );
}

export default Projects;
